import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ResourceLibrary = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { id: 'all', name: 'All Resources', icon: 'Grid' },
    { id: 'assessment', name: 'Assessment Tools', icon: 'BarChart3' },
    { id: 'frameworks', name: 'Frameworks', icon: 'Layers' },
    { id: 'guides', name: 'Implementation Guides', icon: 'BookOpen' },
    { id: 'templates', name: 'Templates', icon: 'FileText' }
  ];

  const resources = [
    {
      id: 1,
      title: 'Engineering Velocity Assessment Tool',
      description: 'Comprehensive framework to measure and benchmark your team\'s development velocity against industry standards.',
      category: 'assessment',
      type: 'Interactive Tool',
      downloadCount: '2.3k',
      rating: 4.9,
      thumbnail: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=300&h=200&fit=crop',
      features: [
        'Sprint velocity analysis',
        'Bottleneck identification',
        'Benchmark comparison',
        'Improvement recommendations'
      ]
    },
    {
      id: 2,
      title: 'Ownership Culture Framework',
      description: 'Step-by-step methodology to transform your engineering team from reactive to proactive ownership mentality.',
      category: 'frameworks',
      type: 'PDF Guide',
      downloadCount: '1.8k',
      rating: 4.8,
      thumbnail: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=300&h=200&fit=crop',
      features: [
        'Cultural assessment matrix',
        'Ownership development stages',
        'Team accountability metrics',
        'Leadership transformation guide'
      ]
    },
    {
      id: 3,
      title: 'Technical Debt Prioritization Matrix',
      description: 'Strategic framework for identifying, categorizing, and prioritizing technical debt to maximize business impact.',
      category: 'frameworks',
      type: 'Excel Template',
      downloadCount: '3.1k',
      rating: 4.7,
      thumbnail: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=300&h=200&fit=crop',
      features: [
        'Impact vs effort analysis',
        'Risk assessment criteria',
        'ROI calculation formulas',
        'Stakeholder communication templates'
      ]
    },
    {
      id: 4,
      title: 'Architectural Modernization Roadmap',
      description: 'Complete guide for planning and executing large-scale architectural transformations without disrupting operations.',
      category: 'guides',
      type: 'Implementation Guide',
      downloadCount: '1.5k',
      rating: 4.9,
      thumbnail: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=300&h=200&fit=crop',
      features: [
        'Migration strategy templates',
        'Risk mitigation frameworks',
        'Team coordination playbooks',
        'Success measurement criteria'
      ]
    },
    {
      id: 5,
      title: 'Team Performance Dashboard Template',
      description: 'Ready-to-use dashboard template for tracking engineering team performance, velocity, and quality metrics.',
      category: 'templates',
      type: 'Dashboard Template',
      downloadCount: '2.7k',
      rating: 4.6,
      thumbnail: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=300&h=200&fit=crop',
      features: [
        'Velocity tracking widgets',
        'Quality metrics visualization',
        'Team health indicators',
        'Automated reporting features'
      ]
    },
    {
      id: 6,
      title: 'Transformation Readiness Checklist',
      description: 'Comprehensive checklist to assess your organization\'s readiness for engineering transformation initiatives.',
      category: 'assessment',
      type: 'Checklist',
      downloadCount: '1.9k',
      rating: 4.8,
      thumbnail: 'https://images.unsplash.com/photo-1586281380349-632531db7ed4?w=300&h=200&fit=crop',
      features: [
        'Organizational readiness assessment',
        'Resource requirement analysis',
        'Risk factor identification',
        'Success probability scoring'
      ]
    }
  ];

  const filteredResources = selectedCategory === 'all' 
    ? resources 
    : resources.filter(resource => resource.category === selectedCategory);

  const handleDownload = (resourceId) => {
    // Mock download functionality
    console.log(`Downloading resource ${resourceId}`);
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-accent/10 rounded-full mb-4">
          <Icon name="BookOpen" size={32} color="var(--color-accent)" />
        </div>
        <h2 className="text-2xl font-bold text-text-primary mb-2">
          Resource Library
        </h2>
        <p className="text-text-secondary max-w-2xl mx-auto">
          Access our collection of proven tools, frameworks, and guides to accelerate your 
          engineering transformation. All resources are based on real-world implementations.
        </p>
      </div>

      {/* Category Filter */}
      <div className="flex flex-wrap justify-center gap-2">
        {categories.map((category) => (
          <button
            key={category.id}
            onClick={() => setSelectedCategory(category.id)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
              selectedCategory === category.id
                ? 'bg-accent text-white' :'bg-muted text-text-secondary hover:bg-accent/10 hover:text-accent'
            }`}
          >
            <Icon name={category.icon} size={16} />
            <span>{category.name}</span>
          </button>
        ))}
      </div>

      {/* Resources Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredResources.map((resource) => (
          <div key={resource.id} className="bg-card rounded-lg border border-border overflow-hidden hover:shadow-lg transition-all">
            <div className="relative">
              <img
                src={resource.thumbnail}
                alt={resource.title}
                className="w-full h-48 object-cover"
              />
              <div className="absolute top-3 left-3">
                <span className="px-2 py-1 bg-accent text-white text-xs font-medium rounded">
                  {resource.type}
                </span>
              </div>
            </div>
            
            <div className="p-6">
              <h3 className="text-lg font-semibold text-text-primary mb-2">
                {resource.title}
              </h3>
              <p className="text-text-secondary text-sm mb-4">
                {resource.description}
              </p>
              
              {/* Features */}
              <div className="mb-4">
                <h4 className="text-sm font-medium text-text-primary mb-2">Key Features:</h4>
                <ul className="space-y-1">
                  {resource.features.slice(0, 3).map((feature, index) => (
                    <li key={index} className="flex items-center space-x-2">
                      <Icon name="Check" size={12} color="var(--color-success)" />
                      <span className="text-xs text-text-secondary">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              {/* Stats */}
              <div className="flex items-center justify-between mb-4 text-sm text-text-secondary">
                <div className="flex items-center space-x-1">
                  <Icon name="Download" size={14} />
                  <span>{resource.downloadCount} downloads</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Icon name="Star" size={14} color="var(--color-warning)" />
                  <span>{resource.rating}</span>
                </div>
              </div>
              
              {/* Download Button */}
              <Button
                variant="default"
                fullWidth
                size="sm"
                className="bg-accent hover:bg-accent/90"
                iconName="Download"
                iconPosition="left"
                onClick={() => handleDownload(resource.id)}
              >
                Download Resource
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Call to Action */}
      <div className="bg-accent/5 rounded-lg border border-accent/20 p-8 text-center">
        <div className="max-w-2xl mx-auto">
          <Icon name="Lightbulb" size={48} color="var(--color-accent)" className="mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-text-primary mb-2">
            Need Customized Resources?
          </h3>
          <p className="text-text-secondary mb-6">
            Our team can create customized assessment tools, frameworks, and implementation guides 
            specifically tailored to your organization's unique challenges and requirements.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              variant="default"
              className="bg-accent hover:bg-accent/90"
              iconName="MessageCircle"
              iconPosition="left"
            >
              Request Custom Resources
            </Button>
            <Button
              variant="outline"
              iconName="Calendar"
              iconPosition="left"
            >
              Schedule Consultation
            </Button>
          </div>
        </div>
      </div>

      {/* Resource Categories Info */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="text-center p-4">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-accent/10 rounded-full mb-3">
            <Icon name="BarChart3" size={24} color="var(--color-accent)" />
          </div>
          <h4 className="font-medium text-text-primary mb-1">Assessment Tools</h4>
          <p className="text-sm text-text-secondary">Measure current state and identify improvement opportunities</p>
        </div>
        <div className="text-center p-4">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-accent/10 rounded-full mb-3">
            <Icon name="Layers" size={24} color="var(--color-accent)" />
          </div>
          <h4 className="font-medium text-text-primary mb-1">Frameworks</h4>
          <p className="text-sm text-text-secondary">Proven methodologies for systematic transformation</p>
        </div>
        <div className="text-center p-4">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-accent/10 rounded-full mb-3">
            <Icon name="BookOpen" size={24} color="var(--color-accent)" />
          </div>
          <h4 className="font-medium text-text-primary mb-1">Implementation Guides</h4>
          <p className="text-sm text-text-secondary">Step-by-step instructions for successful execution</p>
        </div>
        <div className="text-center p-4">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-accent/10 rounded-full mb-3">
            <Icon name="FileText" size={24} color="var(--color-accent)" />
          </div>
          <h4 className="font-medium text-text-primary mb-1">Templates</h4>
          <p className="text-sm text-text-secondary">Ready-to-use documents and dashboards</p>
        </div>
      </div>
    </div>
  );
};

export default ResourceLibrary;